/**
 * ErrorView Component - Estado de Erro da Tela de Resultados
 * @module features/results/components/ErrorView
 *
 * Componente que exibe o estado de erro quando a geracao
 * do relatorio falha.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 *
 * Features:
 * - Mensagem de erro explicativa
 * - Botao de retry
 * - Opcao de iniciar novo assessment
 * - Acessibilidade completa (role="alert", aria-live)
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import type { ErrorViewProps } from '../types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Componente de erro para tela de resultados
 *
 * @param props - Props do componente
 * @param props.error - Mensagem de erro
 * @param props.onRetry - Handler para retry
 * @param props.onNewAssessment - Handler para novo assessment
 *
 * @example
 * ```tsx
 * <ErrorView
 *   error="Falha ao gerar relatorio"
 *   onRetry={() => retry()}
 *   onNewAssessment={() => navigate('/assessment')}
 * />
 * ```
 */
export function ErrorView({ error, onRetry, onNewAssessment }: ErrorViewProps) {
  return (
    <div
      data-testid="error-view"
      role="alert"
      aria-live="assertive"
      className={cn('flex items-center justify-center', 'min-h-[400px] p-8', 'flex-col')}
    >
      <div className="max-w-md text-center space-y-6">
        {/* Error Icon */}
        <div className="flex justify-center">
          <AlertCircle
            data-testid="error-icon"
            className="h-16 w-16 text-destructive"
            aria-hidden="true"
          />
        </div>

        {/* Error Title and Message */}
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">Ops! Algo deu errado</h2>
          <p data-testid="error-message" className="text-muted-foreground">
            {error}
          </p>
        </div>

        {/* Action Buttons */}
        <div data-testid="error-buttons" className="flex flex-col gap-3">
          <Button onClick={onRetry} size="lg" className="w-full" aria-label="Tentar novamente">
            <RefreshCw className="mr-2 h-4 w-4" />
            Tentar Novamente
          </Button>

          <Button
            onClick={onNewAssessment}
            variant="outline"
            size="default"
            className="w-full"
            aria-label="Iniciar novo review"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Iniciar Novo Review
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-muted-foreground">
          Se o problema persistir, entre em contato com nossa equipe.
        </p>
      </div>
    </div>
  );
}

export default ErrorView;
